import { useEffect, useMemo, useState } from 'react'
import { FileText, RefreshCw } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import {
  getApiClient,
  type GiftSummaryAxCompaniesResponse,
  type GiftSummaryAxCompany,
  type GiftSummaryAxMetric,
  type GiftSummaryAxResponse,
  type GiftSummaryDealPacksResponse,
} from '@/lib/api'
import {
  AsOfLabel,
  ErrorRow,
  fmtInt,
  fmtPct,
  LoadingRow,
  PageHeader,
  Section,
  StatusNotice,
  TogglePill,
} from './shared'
import { cn } from '@/lib/utils'

// ─── 期間 ───

const RANGE_OPTIONS = [
  { key: 'this_month', label: '今月' },
  { key: 'this_week', label: '今週' },
  { key: 'last_month', label: '先月' },
  { key: 'all', label: '全期間' },
  { key: 'custom', label: '任意' },
] as const

type RangeKey = (typeof RANGE_OPTIONS)[number]['key']

function isRangeKey(value: string | null): value is RangeKey {
  return RANGE_OPTIONS.some((o) => o.key === value)
}

/** 端末の時計から JST の今日を YYYY-MM-DD で出す（任意期間の初期値用） */
function jstToday(): string {
  return new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10)
}

function ratio(num: number | null | undefined, den: number | null | undefined): number | null {
  if (num === null || num === undefined || den === null || den === undefined) return null
  if (den <= 0) return null
  return num / den
}

function compareLine(value: number | null, prev: number | null): string {
  if (value === null || prev === null) return '前期間 比較なし'
  const diff = value - prev
  if (prev === 0) return `前期間 ${fmtInt(prev)}（増減なし）`
  return `前期間 ${fmtInt(prev)}（${diff >= 0 ? '+' : ''}${fmtInt(diff)}）`
}

// ─── 部品 ───

function UnwiredCard({ title, note }: { title: string; note: string }) {
  return (
    <div className="rounded-md border bg-background p-3">
      <p className="text-sm font-medium">{title}</p>
      <p className="mt-1 text-sm font-semibold text-amber-700">未配線</p>
      <p className="mt-1 text-xs text-muted-foreground">{note}</p>
    </div>
  )
}

function RateCell({
  label,
  value,
  note,
}: {
  label: string
  value: number | null
  note: string
}) {
  return (
    <div className="rounded-md border bg-background p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-bold tabular-nums">{fmtPct(value)}</p>
      <p className="mt-1 text-xs text-muted-foreground">{note}</p>
    </div>
  )
}

/** コール→商談の 6 段カード。段と段の間は転換率 */
function StageStrip({ metrics }: { metrics: GiftSummaryAxMetric[] }) {
  const strip = metrics.slice(0, 6)
  const conversionLabels = ['対話率', '突破率', '代表到達率', 'アポ率', '実施率']

  return (
    <div className="overflow-x-auto">
      <div className="flex min-w-max items-stretch">
        {strip.map((m, i) => {
          const prev = i > 0 ? strip[i - 1] : null
          const conversion = prev ? ratio(m.value, prev.value) : null
          return (
            <div key={m.key} className="flex items-stretch">
              {prev && (
                <div className="flex w-24 shrink-0 flex-col items-center justify-center px-1 text-center">
                  <span className="text-[10px] text-muted-foreground">
                    {conversionLabels[i - 1]}
                  </span>
                  <span className="text-sm font-semibold tabular-nums">
                    {conversion === null ? '—' : fmtPct(conversion)}
                  </span>
                </div>
              )}
              <div className="w-32 shrink-0 rounded-lg border bg-card p-3 shadow-sm">
                <p className="text-xs font-semibold text-muted-foreground">{m.label}</p>
                {!m.wired ? (
                  <p className="mt-1 text-sm font-semibold text-amber-700">未配線</p>
                ) : (
                  <p className="mt-1 text-xl font-bold tabular-nums">
                    {m.value === null ? '未計測' : fmtInt(m.value)}
                  </p>
                )}
                <p className="mt-1 text-[10px] text-muted-foreground">
                  {m.wired ? compareLine(m.value, m.prevValue) : 'この段を数える集計なし'}
                </p>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function CompareChip({
  label,
  value,
  prev,
}: {
  label: string
  value: string
  prev: string
}) {
  return (
    <div className="rounded-md border bg-background p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-bold tabular-nums">{value}</p>
      <p className="mt-1 text-[11px] text-muted-foreground">{prev}</p>
    </div>
  )
}

/** 月次推移の折れ線（SVG）。点が 1 つなら点だけ出す */
function TrendCard({
  label,
  points,
  color,
}: {
  label: string
  points: Array<{ month: string; value: number }>
  color: string
}) {
  const width = 240
  const height = 90
  const padX = 16
  const padY = 12
  const max = Math.max(1, ...points.map((p) => p.value))
  const step = points.length > 1 ? (width - padX * 2) / (points.length - 1) : 0
  const x = (i: number) => padX + i * step
  const y = (v: number) => height - padY - (v / max) * (height - padY * 2)
  const last = points.length > 0 ? points[points.length - 1] : null

  return (
    <div className="rounded-md border bg-background p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-bold tabular-nums">{fmtInt(last?.value ?? null)}</p>
      {points.length === 0 ? (
        <p className="mt-2 text-xs text-muted-foreground">この期間の計測なし</p>
      ) : (
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="mt-1 w-full"
          role="img"
          aria-label={`${label}の月次推移`}
        >
          <polyline
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinejoin="round"
            points={points.map((p, i) => `${x(i)},${y(p.value)}`).join(' ')}
          />
          {points.map((p, i) => (
            <circle key={p.month} cx={x(i)} cy={y(p.value)} r="2.5" fill={color}>
              <title>{`${p.month} ${label} ${p.value}`}</title>
            </circle>
          ))}
        </svg>
      )}
      <p className="text-[10px] text-muted-foreground">
        {points.length > 1 ? `${points[0].month} 〜 ${last?.month ?? ''}` : '1 か月分'}
      </p>
    </div>
  )
}

const TONE_CLASS: Record<GiftSummaryAxCompany['outlookTone'], string> = {
  ok: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  mid: 'border-amber-200 bg-amber-50 text-amber-700',
  ng: 'border-red-200 bg-red-50 text-red-700',
  unknown: 'border-slate-200 bg-slate-50 text-slate-600',
}

/** 各社カード。文字起こしは開いたときだけ取りに行く */
function CompanyCard({ company }: { company: GiftSummaryAxCompany }) {
  const [open, setOpen] = useState(false)
  const [html, setHtml] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!open || html !== null || !company.transcriptFile) return
    let cancelled = false
    setBusy(true)
    setFailed(false)
    void getApiClient()
      .getGiftDealPackHtml(company.pack, company.transcriptFile)
      .then((result) => {
        if (cancelled) return
        setHtml(result?.html ?? null)
        setFailed(result === null)
        setBusy(false)
      })
      .catch(() => {
        if (cancelled) return
        setHtml(null)
        setFailed(true)
        setBusy(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, html, company.pack, company.transcriptFile])

  return (
    <article className="rounded-lg border bg-card p-3 shadow-sm">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold">{company.name}</h3>
          <p className="text-xs text-muted-foreground">
            {company.day ?? '日付不明'}
            {company.timeLabel ? ` ${company.timeLabel}` : ''}
            {company.kind ? ` · ${company.kind}` : ''}
          </p>
        </div>
        <span
          className={cn(
            'rounded-full border px-2 py-0.5 text-xs font-semibold',
            TONE_CLASS[company.outlookTone],
          )}
        >
          {company.outlook || '見通し 未記載'}
        </span>
      </header>

      <p className="mt-2 text-sm">{company.oneLiner || '一言 未記載'}</p>

      <dl className="mt-2 grid gap-1 text-xs text-muted-foreground">
        <div className="flex gap-2">
          <dt className="shrink-0 font-medium">発話比率</dt>
          <dd>{company.speech || '記載なし'}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="shrink-0 font-medium">良い点・悪い点</dt>
          <dd className="text-amber-700">未配線（パックの分析文を構造化して保存する先がまだ無い）</dd>
        </div>
        <div className="flex gap-2">
          <dt className="shrink-0 font-medium">チェック 5 項目</dt>
          <dd className="text-amber-700">未配線</dd>
        </div>
      </dl>

      <details
        className="mt-2 rounded-md border bg-background px-3 py-2"
        onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
      >
        <summary className="cursor-pointer text-sm font-medium">
          文字起こし（全文）
        </summary>
        {!company.transcriptFile ? (
          <p className="mt-2 text-xs text-muted-foreground">このパックに全文がありません</p>
        ) : busy ? (
          <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
            <RefreshCw className="h-3 w-3 animate-spin" aria-hidden />
            読み込み中…
          </div>
        ) : failed || html === null ? (
          <p className="mt-2 text-xs text-muted-foreground">全文を取得できませんでした</p>
        ) : (
          <iframe
            title={`${company.name} の文字起こし`}
            sandbox=""
            srcDoc={html}
            className="mt-2 h-[24rem] w-full border-0"
          />
        )}
      </details>
    </article>
  )
}

// ─── 画面 ───

export function SummaryAxPage() {
  const [params, setParams] = useSearchParams()
  const rangeParam = params.get('range')
  const rangeKey: RangeKey = isRangeKey(rangeParam) ? rangeParam : 'this_month'
  const fromParam = params.get('from') ?? ''
  const toParam = params.get('to') ?? ''

  const [data, setData] = useState<GiftSummaryAxResponse | null>(null)
  const [companies, setCompanies] = useState<GiftSummaryAxCompaniesResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [companiesLoading, setCompaniesLoading] = useState(true)

  const [packs, setPacks] = useState<GiftSummaryDealPacksResponse | null>(null)
  const [selectedPack, setSelectedPack] = useState<string | null>(null)
  const [html, setHtml] = useState<string | null>(null)
  const [packLoading, setPackLoading] = useState(false)

  const query = useMemo(() => {
    const p = new URLSearchParams({ range: rangeKey })
    if (rangeKey === 'custom') {
      if (fromParam) p.set('from', fromParam)
      if (toParam) p.set('to', toParam)
    }
    return p.toString()
  }, [rangeKey, fromParam, toParam])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    void getApiClient()
      .getGiftSummaryAx(query)
      .then((result) => {
        if (cancelled) return
        setData(result)
        setError(false)
        setLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setError(true)
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [query])

  useEffect(() => {
    let cancelled = false
    setCompaniesLoading(true)
    void getApiClient()
      .getGiftSummaryAxCompanies(query)
      .then((result) => {
        if (cancelled) return
        setCompanies(result)
        setCompaniesLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setCompanies(null)
        setCompaniesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [query])

  useEffect(() => {
    let cancelled = false
    void getApiClient()
      .listGiftDealPacks()
      .then((result) => {
        if (!cancelled) setPacks(result)
      })
      .catch(() => {
        // 商談パックの一覧取得失敗は AX 本文を隠さない
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!selectedPack) return
    let cancelled = false
    setPackLoading(true)
    setHtml(null)
    void getApiClient()
      .getGiftDealPackHtml(selectedPack, 'index.html')
      .then((result) => {
        if (cancelled) return
        setHtml(result?.html ?? null)
        setPackLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setHtml(null)
        setPackLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedPack])

  function selectRange(key: RangeKey) {
    const next = new URLSearchParams(params)
    next.set('range', key)
    if (key === 'custom') {
      if (!next.get('from')) next.set('from', `${jstToday().slice(0, 7)}-01`)
      if (!next.get('to')) next.set('to', jstToday())
    } else {
      next.delete('from')
      next.delete('to')
    }
    setParams(next, { replace: true })
  }

  function selectDate(which: 'from' | 'to', value: string) {
    const next = new URLSearchParams(params)
    next.set('range', 'custom')
    if (value) next.set(which, value)
    else next.delete(which)
    setParams(next, { replace: true })
  }

  const metrics = data?.metrics ?? []
  const byKey = useMemo(() => {
    const map: Record<string, GiftSummaryAxMetric> = {}
    for (const m of metrics) map[m.key] = m
    return map
  }, [metrics])
  const monthly = data?.monthly ?? []

  const companiesData = companies
  const companyList = companiesData?.companies ?? []
  const companyPacks = companiesData?.packs ?? []

  return (
    <div className="space-y-6">
      <PageHeader
        title="AX"
        sub="コール成績 → 商談成績 → 分析（全体・各社）。期間を切り替えると同じ範囲で数え直します"
      />

      <div className="flex flex-wrap items-center gap-2">
        {RANGE_OPTIONS.map((option) => (
          <TogglePill
            key={option.key}
            active={rangeKey === option.key}
            onClick={() => selectRange(option.key)}
          >
            {option.label}
          </TogglePill>
        ))}
        {rangeKey === 'custom' && (
          <span className="flex flex-wrap items-center gap-2 text-sm">
            <label className="flex items-center gap-1">
              開始
              <input
                type="date"
                aria-label="開始日"
                value={fromParam}
                onChange={(e) => selectDate('from', e.target.value)}
                className="rounded-md border bg-background px-2 py-1 text-sm"
              />
            </label>
            <label className="flex items-center gap-1">
              終了
              <input
                type="date"
                aria-label="終了日"
                value={toParam}
                onChange={(e) => selectDate('to', e.target.value)}
                className="rounded-md border bg-background px-2 py-1 text-sm"
              />
            </label>
          </span>
        )}
        <span className="ml-auto">
          <AsOfLabel computedAt={data?.computedAt ?? null} />
        </span>
      </div>

      {data?.range.invalid && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          日付の指定が正しくないため、今月を表示しています（終了日は開始日以降にしてください）。
        </p>
      )}

      {loading ? (
        <LoadingRow label="AX" />
      ) : error ? (
        <ErrorRow label="AX" />
      ) : (
        <>
          {/* ① コール成績 */}
          <Section
            title="コール成績"
            aside={<span className="text-xs text-muted-foreground">{data?.range.label}</span>}
            note="段と段の間が転換率。取れない段は 0 ではなく「未配線」「未計測」と出します"
          >
            {data && data.dataStatus === 'not_connected' ? (
              <StatusNotice
                dataStatus="not_connected"
                note="AX_SOURCE_SCOPE と日次集計が接続されると、コール成績が表示されます。"
              />
            ) : (
              <>
                {data && data.dataStatus === 'empty' ? (
                  <p className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    この期間の計測データは 0 件です（例示の数字は入れていません）。
                  </p>
                ) : null}

                <StageStrip metrics={metrics} />

                <div className="mt-3 grid gap-3 md:grid-cols-3">
                  <div className="rounded-md border bg-background p-3">
                    <p className="text-xs text-muted-foreground">
                      商談実施の内訳（代表同席／非同席）
                    </p>
                    <p className="mt-1 text-sm font-semibold text-amber-700">未配線</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      法人商談の同席フラグが Salesforce に無く、自動では数えられません。
                    </p>
                  </div>
                  <RateCell
                    label="クロージング移行率（商談実施 → 2回目）"
                    value={ratio(byKey.cl_meetings?.value, byKey.meetings_held?.value)}
                    note={`商談実施 ${fmtInt(byKey.meetings_held?.value)} → 2回目 ${fmtInt(
                      byKey.cl_meetings?.value,
                    )}`}
                  />
                  <RateCell
                    label="成約率（対クロージング商談）"
                    value={ratio(byKey.closed_won?.value, byKey.cl_meetings?.value)}
                    note={`2回目 ${fmtInt(byKey.cl_meetings?.value)} → 成約 ${fmtInt(
                      byKey.closed_won?.value,
                    )}`}
                  />
                </div>

                <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <CompareChip
                    label="コール"
                    value={fmtInt(byKey.calls?.value)}
                    prev={compareLine(byKey.calls?.value ?? null, byKey.calls?.prevValue ?? null)}
                  />
                  <CompareChip
                    label="アポ率（対コール）"
                    value={fmtPct(ratio(byKey.apo?.value, byKey.calls?.value))}
                    prev="期間内のアポ ÷ コール"
                  />
                  <CompareChip
                    label="商談実施率（対アポ）"
                    value={fmtPct(ratio(byKey.meetings_held?.value, byKey.apo?.value))}
                    prev="期間内の商談実施 ÷ アポ"
                  />
                  <CompareChip
                    label="成約"
                    value={`${fmtInt(byKey.closed_won?.value)}件`}
                    prev={compareLine(
                      byKey.closed_won?.value ?? null,
                      byKey.closed_won?.prevValue ?? null,
                    )}
                  />
                </div>

                <details className="mt-3 rounded-md border bg-background px-3 py-2">
                  <summary className="cursor-pointer text-sm font-medium">
                    段階の定義と一般 BtoB との対応
                  </summary>
                  <ul className="mt-2 grid gap-1 text-xs text-muted-foreground">
                    <li>コール＝Dial／対話＝Connect（相手が出て会話）／フロント突破＝受付を通った</li>
                    <li>
                      代表到達＝Decision maker 到達。画面では「代表到達」と書き、商談側の
                      「クロージング」とは別の段として扱う
                    </li>
                    <li>アポ＝日時確定／商談実施＝Show／2回目＝クロージング商談／成約＝Close</li>
                    <li>
                      フロント突破・代表到達・同席は通話結果コードからの自動集計が未着手のため未配線
                    </li>
                  </ul>
                </details>
              </>
            )}
          </Section>

          {/* ② 商談成績 */}
          <Section
            title="商談成績"
            note="アポした商談がいまどこにいるか。SF のステージ別件数と、まだ数えられていない項目を分けて出します"
          >
            <div className="rounded-md border bg-background p-3">
              <p className="text-sm font-medium">
                状態ストリップ
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  {data?.pipelineDay ?? '日付 未計測'} 時点
                </span>
              </p>
              {data && data.pipeline.length > 0 ? (
                <div className="mt-2 overflow-x-auto">
                  <div className="flex min-w-max gap-2">
                    {data.pipeline.map((p) => (
                      <div
                        key={p.stage}
                        className="w-32 shrink-0 rounded-lg border bg-card p-3 text-center"
                      >
                        <p className="text-xl font-bold tabular-nums">{fmtInt(p.deals)}</p>
                        <p className="text-xs font-medium">{p.stage}</p>
                        <p className="mt-1 text-[10px] text-muted-foreground">
                          見込 {p.quoteAmountYen > 0 ? `${fmtInt(p.quoteAmountYen)}円` : '未入力'}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : data?.dataStatus === 'not_connected' ? (
                <p className="mt-2 text-sm text-amber-700">
                  未接続：AX_SOURCE_SCOPE と日次集計が接続されると、ステージ別の件数が出ます
                </p>
              ) : (
                <p className="mt-2 text-sm text-amber-700">
                  未計測：この期間に pipeline 軸の日次集計がありません
                </p>
              )}
              <p className="mt-2 text-xs text-muted-foreground">
                SF のステージ名をそのまま出しています。「商談待ち」「初回NG」などへの言い換えは未配線です。
              </p>
            </div>

            <div className="mt-3 grid gap-3 md:grid-cols-3">
              <UnwiredCard
                title="NG理由"
                note="失注理由と来なかった理由が日次集計に載っていません。"
              />
              <UnwiredCard
                title="会話比率（GIFT 側の発話％）"
                note="話者（GIFT 側／相手側）の対応づけが未配線です。商談パックに書かれた比率は各社カードに出します。"
              />
              <UnwiredCard
                title="チェックリスト合格率（1回目 5 項目）"
                note="5 項目の判定を保存する先がまだありません。"
              />
            </div>
          </Section>

          {/* ③ 分析・全体 */}
          <Section title="分析・全体" note="推移・方法の変遷・精度・課題">
            <h3 className="text-sm font-semibold">推移（月次）</h3>
            <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <TrendCard
                label="コール"
                color="#0f766e"
                points={monthly.map((m) => ({ month: m.month, value: m.calls }))}
              />
              <TrendCard
                label="アポ"
                color="#0e7490"
                points={monthly.map((m) => ({ month: m.month, value: m.apo }))}
              />
              <TrendCard
                label="商談実施"
                color="#4338ca"
                points={monthly.map((m) => ({ month: m.month, value: m.meetingsHeld }))}
              />
              <TrendCard
                label="成約"
                color="#15803d"
                points={monthly.map((m) => ({ month: m.month, value: m.closedWon }))}
              />
            </div>
            {monthly.length <= 1 && (
              <p className="mt-2 text-xs text-muted-foreground">
                選択期間の月が 1 つだけのため、推移は 1 点です。「全期間」にすると複数月が並びます。
              </p>
            )}

            <h3 className="mt-4 text-sm font-semibold">アプローチの変遷</h3>
            <div className="mt-2 grid gap-3 md:grid-cols-2">
              <div className="rounded-md border bg-background p-3">
                <p className="text-xs font-medium text-muted-foreground">コール</p>
                <ol className="mt-2 flex flex-wrap items-center gap-1 text-xs">
                  {['取材フック', '電話は取材の話だけ', 'アポは代表のみ', '受付は「アポ前提」'].map(
                    (label, i, arr) => (
                      <li key={label} className="flex items-center gap-1">
                        <span
                          className={cn(
                            'rounded-md border px-2 py-1',
                            i === arr.length - 1
                              ? 'border-primary bg-primary/10 font-semibold text-primary'
                              : '',
                          )}
                        >
                          {label}
                        </span>
                        {i < arr.length - 1 && <span className="text-muted-foreground">›</span>}
                      </li>
                    ),
                  )}
                </ol>
              </div>
              <div className="rounded-md border bg-background p-3">
                <p className="text-xs font-medium text-muted-foreground">商談</p>
                <ol className="mt-2 flex flex-wrap items-center gap-1 text-xs">
                  {['1回目＝深く聞く', '2回目＝クロージング', 'まず月20万を提示'].map(
                    (label, i, arr) => (
                      <li key={label} className="flex items-center gap-1">
                        <span
                          className={cn(
                            'rounded-md border px-2 py-1',
                            i === arr.length - 1
                              ? 'border-primary bg-primary/10 font-semibold text-primary'
                              : '',
                          )}
                        >
                          {label}
                        </span>
                        {i < arr.length - 1 && <span className="text-muted-foreground">›</span>}
                      </li>
                    ),
                  )}
                </ol>
              </div>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              決定の番号と根拠は週次会議に集約しています（この画面では再掲しません）。
            </p>

            <h3 className="mt-4 text-sm font-semibold">精度</h3>
            <div className="mt-2 grid gap-3 md:grid-cols-2">
              <UnwiredCard
                title="戦略精度"
                note="型・価格・ターゲットの一貫性を採点する保存先がまだありません。"
              />
              <UnwiredCard
                title="実行精度"
                note="記録の穴・次回確定・発話バランスを採点する保存先がまだありません。"
              />
            </div>

            <h3 className="mt-4 text-sm font-semibold">課題（数字の現在地）</h3>
            <div className="mt-2 grid gap-3 md:grid-cols-3">
              <CompareChip
                label="アポ率（対コール）"
                value={fmtPct(ratio(byKey.apo?.value, byKey.calls?.value))}
                prev={compareLine(byKey.apo?.value ?? null, byKey.apo?.prevValue ?? null)}
              />
              <CompareChip
                label="クロージング移行率"
                value={fmtPct(ratio(byKey.cl_meetings?.value, byKey.meetings_held?.value))}
                prev={compareLine(
                  byKey.cl_meetings?.value ?? null,
                  byKey.cl_meetings?.prevValue ?? null,
                )}
              />
              <CompareChip
                label="成約"
                value={`${fmtInt(byKey.closed_won?.value)}件`}
                prev={compareLine(
                  byKey.closed_won?.value ?? null,
                  byKey.closed_won?.prevValue ?? null,
                )}
              />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              打ち手の提案は未配線です（週次会議で決めます）。
            </p>
          </Section>

          {/* ④ 分析・各社 */}
          <Section
            title="分析・各社"
            aside={
              <span className="text-xs text-muted-foreground">
                {companiesData
                  ? `${companiesData.fromDay} 〜 ${companiesData.toDay}`
                  : '期間 未計測'}
              </span>
            }
            note="GIFT_DEAL_PACKS_DIR の商談パック（一覧表）から、選択期間に入る商談を出しています"
          >
            {companiesLoading ? (
              <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
                <RefreshCw className="h-4 w-4 animate-spin" aria-hidden />
                各社を読み込み中…
              </div>
            ) : !companiesData || companiesData.dataStatus === 'not_connected' ? (
              <StatusNotice
                dataStatus="not_connected"
                note="未接続：本番で GIFT_DEAL_PACKS_DIR の読み取り専用マウントが必要です。"
              />
            ) : companyList.length === 0 ? (
              <p className="py-2 text-sm text-muted-foreground">
                この期間の商談パックはありません
              </p>
            ) : (
              <>
                {companiesData.periodMismatch && (
                  <p className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    商談パックは期間のまとまりで作られるため、画面の期間とは端がずれます。ずれた分は含めていません。
                  </p>
                )}
                {companiesData.undated > 0 && (
                  <p className="mb-3 text-xs text-muted-foreground">
                    日付を読めなかった行が {companiesData.undated} 件あります（期間の判定から除いています）。
                  </p>
                )}
                <div className="grid gap-3 md:grid-cols-2">
                  {companyList.map((c) => (
                    <CompanyCard key={`${c.pack}-${c.name}-${c.timeLabel ?? ''}`} company={c} />
                  ))}
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  対象パック: {companyPacks.map((p) => p.name).join('、')}
                </p>
              </>
            )}
          </Section>

          {/* 原本（必要なときだけ開く） */}
          <Section
            title="商談パック（原本）"
            note="GIFT_DEAL_PACKS_DIR の HTML。スクリプト無効の sandbox iframe で表示します"
          >
            {!packs || packs.dataStatus === 'not_connected' ? (
              <StatusNotice
                dataStatus="not_connected"
                note="未接続：本番で GIFT_DEAL_PACKS_DIR の読み取り専用マウントが必要です。"
              />
            ) : packs.packs.length === 0 ? (
              <StatusNotice dataStatus="empty" />
            ) : (
              <div className="grid gap-3 md:grid-cols-[16rem_1fr]">
                <ul className="grid gap-1">
                  {packs.packs.map((p) => (
                    <li key={p.name}>
                      <button
                        type="button"
                        onClick={() => setSelectedPack(p.name)}
                        className={cn(
                          'flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left text-sm',
                          selectedPack === p.name
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'text-muted-foreground hover:bg-accent',
                        )}
                      >
                        <FileText className="h-4 w-4 shrink-0" aria-hidden />
                        {p.name}
                      </button>
                    </li>
                  ))}
                </ul>
                <div className="min-h-[20rem] rounded-md border bg-background">
                  {selectedPack === null ? (
                    <p className="p-4 text-sm text-muted-foreground">パックを選択してください</p>
                  ) : packLoading ? (
                    <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
                      <RefreshCw className="h-4 w-4 animate-spin" aria-hidden />
                      読み込み中…
                    </div>
                  ) : html === null ? (
                    <p className="p-4 text-sm text-muted-foreground">
                      このパックに表示できる HTML（index.html）がありません
                    </p>
                  ) : (
                    <iframe
                      title={`商談パック ${selectedPack}`}
                      sandbox=""
                      srcDoc={html}
                      className="h-[32rem] w-full border-0"
                    />
                  )}
                </div>
              </div>
            )}
          </Section>
        </>
      )}
    </div>
  )
}
